/**
 * shared/decisions.ts — the Decisions primitive (parent card VcBi3nr9GjBj,
 * child A JLkBEaxDx6As).
 *
 * A decision is a couple-authored two-option choice. Each partner picks in
 * private, and the same card reveals both picks together. The SAME shape is
 * served to both partners; only the seal differs (see projectDecision).
 *
 * Seal rule (AC2 of the parent card): while a decision is `pending`, the
 * response carries WHO has voted and never WHAT they picked — no choices, no
 * counts, no per-option tallies. Your own pick is echoed back to you so the
 * UI can show "Locked in" on the option you chose; the partner's is absent
 * entirely, not blanked in the client. The projection below is the ONLY place
 * that decides this, and it runs server-side before the row leaves the room.
 *
 * Options are always visible: a person cannot vote on text they cannot read.
 * The sealed thing is the vote, not the question.
 */

export const DECISION_STATES = ['pending', 'revealed', 'tied'] as const;
export type DecisionState = (typeof DECISION_STATES)[number];

/**
 * The LEGACY two-option choice ids. Kept because the Thread and Life screens
 * still render `a`/`b`, and because `options.a`/`options.b` remain on the wire
 * (card ZTe1ndjRvT32 keeps the change additive). New answers use the option
 * id — the position, as a decimal string, `'1'`..`'n'` — see DecisionAnswer.
 */
export const DECISION_CHOICES = ['a', 'b'] as const;
export type DecisionChoice = (typeof DECISION_CHOICES)[number];

/**
 * The catchall answer (Jonathan's ruling 2026-11-04 17:37): "any of these
 * works, or here is when I am free" — optional free text plus optional
 * calendar entries. It is a SEPARATE answer and never a vote for any option,
 * which is the whole point of retiring "Either works" (that verb read as a
 * preference when it means indifference).
 */
export const DECISION_OPEN_ANSWER = 'open';

/** One option, as answered and rendered: (label, optional text, optional
 *  date). Every option can carry text and a date — including the first two,
 *  so "Lock in" is not a bare id either (ruling 2026-11-04 17:37). */
export interface DecisionOptionView {
  /** Stable id inside the decision: the position as a decimal string. */
  id: string;
  label: string;
  text: string | null;
  at: number | null;
}

/** A stored option row (decision_options), or one derived for a legacy
 *  decision whose two options still live in decisions.option_a/option_b. */
export interface DecisionOptionRow {
  position: number;
  label: string;
  text?: string | null;
  at?: number | null;
}

/** An answer is the id of the option that person picked, or the open
 *  sentinel. Never a boolean, never "which of two". */
export type DecisionAnswer = string;

/** Read an answer off a stored vote row, tolerating the legacy choice column
 *  for couples whose votes predate the answer column. */
export function answerIdOf(row: { answer?: string | null; choice?: string | null }): DecisionAnswer {
  const a = String(row.answer ?? '').trim();
  if (a) return a;
  return String(row.choice ?? '').trim() === 'b' ? '2' : '1';
}

/** The full option list of a decision: stored rows when there are any, else
 *  the legacy pair derived from the decisions row. Positions 1 and 2 are the
 *  authored pair either way, so a renderer needs no special case. */
export function decisionOptionRows(rec: {
  option_a: string;
  option_b: string;
  options?: DecisionOptionRow[];
}): DecisionOptionView[] {
  const stored = (rec.options ?? []).filter(o => Number.isFinite(o.position));
  const base: DecisionOptionRow[] = stored.length > 0
    ? [...stored].sort((x, y) => x.position - y.position)
    : [
        { position: 1, label: rec.option_a, text: null, at: null },
        { position: 2, label: rec.option_b, text: null, at: null },
      ];
  const seen = new Set<number>();
  const out: DecisionOptionView[] = [];
  for (const o of base) {
    const pos = Number(o.position);
    if (!Number.isFinite(pos) || pos < 1 || seen.has(pos)) continue;
    seen.add(pos);
    out.push({
      id: String(pos),
      label: String(o.label ?? ''),
      text: o.text == null || String(o.text).trim() === '' ? null : String(o.text),
      at: o.at == null || !Number.isFinite(Number(o.at)) ? null : Number(o.at),
    });
  }
  return out;
}

/** Card verbs, locked 2026-10-20. Never "Vote", never "Poll". */
export const DECISION_VERBS: Readonly<Record<DecisionChoice, string>> = {
  a: 'Lock in',
  b: 'Either works',
};

/** One nudge inside the push nuisance budget, then "still undecided" at 48h. */
export const DECISION_STALL_MS = 48 * 60 * 60 * 1000;
export const DECISION_NUDGE_MS = 24 * 60 * 60 * 1000;

/**
 * What the 48h rule does to one pending decision at a given clock (card
 * bwcTJh8Od5rk). Pure, so the sweep (which WRITES), the report (which only
 * reads) and a probe all answer the same question from one definition:
 *
 *   none  — too young, or already nudged inside the nudge window
 *   nudge — past the nudge window, no nudge spent yet: spend it once
 *   stall — past 48h: file the agenda talking point, never close the decision
 *
 * The stall is idempotent by construction (nudged once, so `alreadyNudged`
 * keeps returning 'none' for the nudge half) — the agenda marker table is what
 * makes the FILE half safe to repeat.
 */
export type DecisionStallAction = 'none' | 'nudge' | 'stall';

export function stallAction(input: { createdAt: number; nudgedAt: number | null; now: number }): DecisionStallAction {
  const age = input.now - input.createdAt;
  if (age >= DECISION_STALL_MS) return 'stall';
  if (age >= DECISION_NUDGE_MS && !input.nudgedAt) return 'nudge';
  return 'none';
}

/** How a decision came to exist. Recorded on every creation (AC6). */
export type DecisionInitiationSource = 'typed_message' | 'composer' | 'date_needs_time' | 'request_stall' | 'inbox_proposal';

export interface DecisionVoteView {
  memberId: string;
  /** Present only once the decision is revealed/tied, or on your own vote while pending. */
  choice?: DecisionChoice;
  /** The answer itself — an option id, or 'open'. Sealed exactly like choice:
   *  absent for the partner while the decision is pending. */
  answer?: DecisionAnswer;
  /** The answer's own free text and date (both optional, both sealed). */
  text?: string | null;
  at?: number | null;
  mine: boolean;
  votedAt: number;
}

export interface DecisionView {
  decisionId: string;
  /** The generic messages.round_id this card renders through — never a new message kind. */
  roundId: string;
  authorId: string;
  title: string;
  /** The legacy pair, still on the wire for the current render. */
  options: { a: string; b: string };
  /** Every option, in authored order: (id, label, optional text, optional
   *  date). Superset of `options` — positions 1 and 2 are the same two
   *  labels. Card ZTe1ndjRvT32. */
  choices: DecisionOptionView[];
  state: DecisionState;
  createdAt: number;
  resolvedAt: number | null;
  /** pending and past the 48h stall window: still openable, nudging stopped. */
  stalled: boolean;
  /** Both answered "I'm open": the decision does not settle and needs a
   *  decision (Jonathan's ruling 2026-11-04 17:37). */
  needsDecision: boolean;
  /** Set when the decision settled on an option: that option's id. */
  winnerOptionId: string | null;
  initiationSource: DecisionInitiationSource;
  votes: DecisionVoteView[];
  /** Set once resolved: both picks, or the tie line. Null while pending. */
  outcomeLine: string | null;
  /** Agent-proposed options beyond the couple's two (Plus only). Empty for the
   *  free two-option card, which is the launch shape. */
  extraOptions: string[];
}

export interface DecisionRecord {
  decision_id: string;
  round_id: string;
  author_id: string;
  title: string;
  option_a: string;
  option_b: string;
  initiation_source: string;
  outcome_text: string | null;
  state: string;
  created_at: number;
  resolved_at: number | null;
  nudged_at: number | null;
}

export interface DecisionVoteRow {
  decision_id: string;
  member_id: string;
  /** Legacy column: 'a'|'b', only meaningful for positions 1 and 2. */
  choice?: string;
  /** The answer: an option id, or 'open'. */
  answer?: string;
  answer_text?: string | null;
  answer_at?: number | null;
  voted_at: number;
}

function normalizeState(raw: unknown): DecisionState {
  const v = String(raw ?? '');
  return (DECISION_STATES as readonly string[]).includes(v) ? (v as DecisionState) : 'pending';
}

function normalizeSource(raw: unknown): DecisionInitiationSource {
  const v = String(raw ?? '');
  return v === 'composer' || v === 'date_needs_time' || v === 'request_stall' || v === 'inbox_proposal'
    ? (v as DecisionInitiationSource)
    : 'typed_message';
}

/**
 * The seal. Pure so the room and any test can share one definition.
 * `viewerId` is the member asking; the other member's choice is stripped
 * while the decision is pending.
 */
export function projectDecision(
  rec: DecisionRecord,
  votes: DecisionVoteRow[],
  viewerId: string,
  now: number = Date.now(),
  optionRows: DecisionOptionRow[] = [],
): DecisionView {
  const state = normalizeState(rec.state);
  const resolved = state !== 'pending';
  const choices = decisionOptionRows({ option_a: rec.option_a, option_b: rec.option_b, options: optionRows });
  const byId = new Map(choices.map(o => [o.id, o]));
  const sealed: DecisionVoteView[] = votes.map(v => {
    const mine = v.member_id === viewerId;
    const base: DecisionVoteView = { memberId: v.member_id, mine, votedAt: v.voted_at };
    const answer = answerIdOf(v as unknown as { answer?: string | null; choice?: string | null });
    if (resolved || mine) {
      base.answer = answer;
      /* Legacy field: positions 1/2 still express themselves as a/b so the
         shipped Thread and Life render keep working untouched. */
      if (answer === '1') base.choice = 'a';
      else if (answer === '2') base.choice = 'b';
      base.text = (v as unknown as { answer_text?: string | null }).answer_text ?? null;
      base.at = (v as unknown as { answer_at?: number | null }).answer_at ?? null;
    }
    return base;
  });

  let outcomeLine: string | null = null;
  let winnerOptionId: string | null = null;
  if (state === 'revealed') {
    outcomeLine = rec.outcome_text ?? 'Both of you picked.';
    const counts = new Map<string, number>();
    for (const v of sealed) if (v.answer && v.answer !== DECISION_OPEN_ANSWER) counts.set(v.answer, (counts.get(v.answer) ?? 0) + 1);
    const top = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
    winnerOptionId = top ? top[0] : null;
  } else if (state === 'tied') {
    outcomeLine = rec.outcome_text ?? 'You picked differently — it is on the Plan agenda.';
  }

  /* Both answered "I'm open": nothing to settle, and the card says so rather
     than pretending one of them leaned somewhere. */
  const names = new Set(votes.map(v => v.member_id));
  const allOpen =
    votes.length >= 2 &&
    names.size >= 2 &&
    votes.every(v => answerIdOf(v as unknown as { answer?: string | null; choice?: string | null }) === DECISION_OPEN_ANSWER);

  return {
    decisionId: rec.decision_id,
    roundId: rec.round_id,
    authorId: rec.author_id,
    title: rec.title,
    options: {
      a: byId.get('1')?.label ?? rec.option_a,
      b: byId.get('2')?.label ?? rec.option_b,
    },
    choices,
    state,
    createdAt: rec.created_at,
    resolvedAt: rec.resolved_at ?? null,
    stalled: state === 'pending' && now - rec.created_at >= DECISION_STALL_MS,
    needsDecision: state === 'pending' && allOpen,
    winnerOptionId,
    initiationSource: normalizeSource(rec.initiation_source),
    votes: sealed,
    outcomeLine,
    extraOptions: choices.filter(o => Number(o.id) > 2).map(o => o.label),
  };
}

/**
 * Typed-message arming (child B, card HMzNgJETH7HE).
 *
 * The composer stays nine tiles: the trigger for a decision is the shape of
 * what a person already typed, not a menu (parent card VcBi3nr9GjBj, LOCKED
 * 2026-10-20). This is that shape, deterministic and shared so the chip the
 * Thread offers and the title/options the server stores can never disagree:
 *
 *   two-option  "Pizza or pasta?" / "Should we fly or drive?"  -> matches
 *   favour      "Can you pick up milk?"                        -> null (Requests)
 *   plain       "Dinner at 7"                                  -> null
 *
 * Two options only. A sentence that splits on a second "or" has three, so it
 * returns null rather than arming a card with a silently dropped option.
 */
export interface DecisionIntent {
  title: string;
  optionA: string;
  optionB: string;
}

/** Favour shape is Requests' trigger (card o4i7UUbHTn5M), never a decision. */
const FAVOUR_SHAPE = /^(can|could|would|will|shall|may)\s+you\b/i;

/** Question stems folded into the title, not into the first option. */
const STEM_SHAPE = /^(should we|shall we|do we|don't we|are we|is it|can we|could we|would we|will we)\s+/i;

const TRAILING_PUNCT = /[.?!,;:]+$/;

export function parseDecisionIntent(raw: string): DecisionIntent | null {
  const text = raw.trim().replace(/\s+/g, ' ');
  if (!text || text.length > 200) return null;
  if (FAVOUR_SHAPE.test(text)) return null;

  const split = text.match(/^(.+?)\s+or\s+(.+)$/i);
  if (!split) return null;
  const left = split[1].trim();
  const right = split[2].trim();
  /* Three or more options is not a decision at launch — it is a dropped one. */
  if (/\s+or\s+/i.test(right)) return null;

  const optionA = left.replace(STEM_SHAPE, '').trim().replace(TRAILING_PUNCT, '');
  const optionB = right.replace(TRAILING_PUNCT, '');
  if (!optionA || !optionB) return null;
  if (optionA.length > 60 || optionB.length > 60) return null;
  if (optionA.toLowerCase() === optionB.toLowerCase()) return null;

  const title = text.replace(TRAILING_PUNCT, '');
  if (!title) return null;
  return { title, optionA, optionB };
}

/**
 * Resolve a decision from its members' ANSWERS (card ZTe1ndjRvT32), not from
 * two booleans. Jonathan's rule, 2026-11-04 17:37:
 *
 *   - a majority on one option wins;
 *   - "I'm open" never counts as a vote for any option — it removes that
 *     person from the split;
 *   - one picked and the other open settles ON the pick;
 *   - both open does not settle: the decision stays open and needs a decision
 *     (returns null, which is also "still waiting" — the caller distinguishes
 *     the two from the answers, never from the return).
 *
 * Returns null while fewer than two answers exist, and null when every answer
 * is the open sentinel.
 */
export function settleDecision(
  answers: DecisionAnswer[],
  optionRows: DecisionOptionRow[] = [],
): { state: 'revealed' | 'tied'; outcome: string; winnerOptionId: string | null } | null {
  if (answers.length < 2) return null;
  const picks = answers.filter(a => a !== DECISION_OPEN_ANSWER);
  if (picks.length === 0) return null;

  const labelOf = (id: string) => {
    const row = optionRows.find(o => String(o.position) === id);
    return row && String(row.label ?? '').trim() !== '' ? String(row.label) : `option ${id}`;
  };
  const counts = new Map<string, number>();
  for (const p of picks) counts.set(p, (counts.get(p) ?? 0) + 1);
  const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  const [topId, topCount] = ranked[0];
  const runnerUp = ranked[1];

  if (runnerUp && runnerUp[1] === topCount) {
    return { state: 'tied', outcome: 'You picked differently — it is on the Plan agenda.', winnerOptionId: null };
  }

  const unanimous = picks.length >= 2 && topCount === picks.length;
  const outcome = unanimous
    ? `You both picked “${labelOf(topId)}”.`
    : `You picked “${labelOf(topId)}” — the other was open to anything.`;
  return { state: 'revealed', outcome, winnerOptionId: topId };
}

/* ── Couple-level surfaces (child C, card bwcTJh8Od5rk) ───────────────────
 * Everything below is pure so the Life rail, the worker and a probe read ONE
 * definition of "what the rail shows" and "what the meeting is told". */

/**
 * The line a tied or stalled decision files onto the weekly meeting's agenda.
 * It carries the QUESTION, never a pick: the agenda is a talking point for the
 * couple, so neither partner's choice is on it (that stays in the sealed
 * decision, which both can still open).
 */
export function decisionAgendaText(view: Pick<DecisionView, 'title' | 'state'>): string {
  return view.state === 'tied'
    ? `Decisions — you picked differently on “${view.title}”`
    : `Decisions — “${view.title}” is still undecided after 48 hours`;
}

export interface DecisionRailSelection {
  /** Open decisions, newest first. */
  open: DecisionView[];
  /** The ONE open decision the Life rail may draw, or null. */
  visible: DecisionView | null;
  /** Every open decision — this is what the Thread rail's count includes. */
  openCount: number;
  /** Settled decisions (revealed / tied), newest first. */
  resolved: DecisionView[];
}

/** At most one open decision on the Life rail; the rest is a count, not a
 *  stack (parent card AC7). Resolved rows never take a rail slot. */
export function selectDecisionRail(views: DecisionView[]): DecisionRailSelection {
  const sorted = [...views].sort((a, b) => b.createdAt - a.createdAt);
  const open = sorted.filter(v => v.state === 'pending');
  const resolved = sorted.filter(v => v.state !== 'pending');
  return { open, visible: open[0] ?? null, openCount: open.length, resolved };
}

/**
 * The needs_time route's two candidate times (parent AC6): the next date slot
 * becomes the first option, the one after it the second. Both are prefilled —
 * a person picks a time, never types one. Local time, because "Thursday
 * evening" is the couple's evening, not the worker's UTC.
 */
export function dateTimeCandidates(now: number = Date.now()): { a: string; b: string } {
  const DAY = 86_400_000;
  const label = (t: number) =>
    new Date(t).toLocaleString(undefined, { weekday: 'long', hour: 'numeric', minute: '2-digit' });
  const d = new Date(now);
  d.setHours(18, 30, 0, 0);
  /* Next Thursday 6:30pm — the preferred slot (shared/date-nudge.ts). */
  while (d.getDay() !== 4 || d.getTime() <= now) d.setTime(d.getTime() + DAY);
  const a = d.getTime();
  /* Then the next Saturday 11:00 — the fallback slot, a real second choice. */
  d.setDate(d.getDate() + 1);
  d.setHours(11, 0, 0, 0);
  while (d.getDay() !== 6) d.setTime(d.getTime() + DAY);
  return { a: label(a), b: label(d.getTime()) };
}

/** Two options are free forever; a third and beyond is an agent-proposed extra
 *  and sits behind Plus (parent card, locked rule-free / agent-Plus pattern).
 *  Server-side only — a client cannot buy itself a gate by sending one. */
export const DECISION_FREE_OPTION_LIMIT = 2;
